import ApplicationModel from './ApplicationModel';
import Fragment from './Fragment';

type Callback = (data: unknown, error?: unknown) => void;
export type BatchResponse = Record<string, any>;

type Options = {
  mutate: boolean;
  reactivelyUpdate?: boolean;
};

export class Batch {
  _fragments: Fragment[];
  _aliases: string[];
  _callbacks: Record<string, Callback[]>;
  _executed: boolean;
  _data: BatchResponse;
  _options: Options;

  constructor(options: Options = { mutate: false }) {
    this._fragments = [];
    this._aliases = [];
    this._callbacks = {};
    this._executed = false;
    this._options = options;
  }

  get type() {
    return this.mutate ? 'mutation' : 'query';
  }

  get mutate() {
    return this._options.mutate;
  }

  get length() {
    return this._fragments.length;
  }

  get executed() {
    return this._executed;
  }

  add(fragment: Fragment, callback: Callback) {
    if (this.executed) throw new Error('This batch has already executed');

    // If any of the fragments being batched can be merged together then merge
    // them and combine the callbacks
    if (!this.mutate) {
      const mergeResult = this.tryMergeFragments(fragment, callback);
      if (mergeResult) return mergeResult;
    }

    return this.addFragment(fragment, callback);
  }

  tryMergeFragments(fragment: Fragment, callback: Callback) {
    for (let index = 0; index < this._fragments.length; index++) {
      const existing = this._fragments[index];
      if (existing.name === fragment.name) {
        // It's faster to catch an error than to check if a merge is possible
        try {
          const merged = this._fragments[index].merge(fragment);
          const alias = existing._alias;
          this._fragments[index] = merged;
          this._callbacks[alias].push(callback);
          return alias;
        } catch (err) {}
      }
    }

    return null;
  }

  addFragment(fragment: Fragment, callback: Callback) {
    const alias = `${this.type}_${this._fragments.length}`;
    this._aliases.push(alias);
    this._fragments.push(fragment.alias(alias));
    this._callbacks[alias] = [callback];
    return alias;
  }

  get fragment() {
    let fragment = this._fragments.reduce((acc, f) => {
      return acc.fragment(f);
    }, new Fragment());

    if (this._options.reactivelyUpdate) {
      fragment = fragment.argument('__reactivelyUpdate', true);
    }

    return fragment;
  }

  async execute() {
    if (this.executed) throw new Error('This batch has already executed');
    this._executed = true;

    // If there's nothing to do return early
    if (this._fragments.length === 0) return [];

    const resolvedFragment = this.fragment.resolve();
    const returnedData = [];

    try {
      const variables = resolvedFragment.toVariables();
      if (this.mutate) {
        this._data = await ApplicationModel.client.mutate(
          resolvedFragment.toDocument('MutateBatch', 'mutation'),
          {
            variables,
          }
        );
      } else {
        this._data = await ApplicationModel.client.query(
          resolvedFragment.toDocument('GetBatch', 'query'),
          {
            variables,
          }
        );
      }
    } catch (err) {
      // If an error occurs during fetch then spread it to all waiting listeners
      for (let alias of this._aliases) {
        const callbacks = this._callbacks[alias];
        callbacks.forEach(callback => callback(null, err));
      }

      // Re-raise the error to stop the flow here
      throw err;
    }

    for (let alias of this._aliases) {
      const callbacks = this._callbacks[alias];
      const aliasData = this._data[alias];
      if (aliasData) {
        callbacks.forEach(callback => returnedData.push(callback(aliasData)));
      } else {
        callbacks.forEach(callback =>
          callback(null, new Error(`No data ${alias}`))
        );
      }
    }

    return returnedData;
  }
}
